import {
  AlertTriangle,
  Clock,
  User,
  Users2,
} from 'lucide-react';

interface FlaggedMessage {
  id: number;
  squadId: number;
  squadName: string;
  senderId: number;
  senderName: string;
  senderAvatarUrl?: string;
  messageText: string;
  aiFlagged: boolean;
  aiToxicityScore?: number;
  aiFlagReason?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  createdAt: string;
}

interface FlaggedMessageCardProps {
  message: FlaggedMessage;
  onClick: () => void;
  isSelected?: boolean;
}

export function FlaggedMessageCard({ message, onClick, isSelected }: FlaggedMessageCardProps) {
  // Get severity config
  const getSeverityConfig = () => {
    switch (message.severity) {
      case 'CRITICAL':
        return { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/50' };
      case 'HIGH':
        return { color: 'text-orange-400', bg: 'bg-orange-500/20', border: 'border-orange-500/50' };
      case 'MEDIUM':
        return { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/50' };
      case 'LOW':
      default:
        return { color: 'text-slate-400', bg: 'bg-slate-500/20', border: 'border-slate-500/50' };
    }
  };

  const { color, bg, border } = getSeverityConfig();

  // Format time ago
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  // Format toxicity score as percentage
  const formatScore = (score?: number) => {
    if (score === undefined || score === null) return 'N/A';
    return `${Math.round(score * 100)}%`;
  };

  return (
    <div
      onClick={onClick}
      className={`p-4 border rounded-lg cursor-pointer transition-all ${
        isSelected
          ? 'border-cyan-500 bg-cyan-500/10'
          : `${border} bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800`
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${bg}`}>
            <AlertTriangle className={`h-4 w-4 ${color}`} />
          </div>
          <div>
            <span className={`text-xs font-medium uppercase ${color}`}>
              {message.severity}
            </span>
            <span className="text-xs text-slate-500 ml-2">
              Score: {formatScore(message.aiToxicityScore)}
            </span>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${bg} ${color}`}>
          {message.aiFlagReason || 'FLAGGED'}
        </span>
      </div>

      {/* Message content */}
      <div className="mb-3 p-3 bg-slate-900/50 rounded border border-slate-700">
        <div className="flex items-center gap-2 mb-2">
          {message.senderAvatarUrl ? (
            <img
              src={message.senderAvatarUrl}
              alt={message.senderName}
              className="h-6 w-6 rounded-full"
            />
          ) : (
            <div className="h-6 w-6 rounded-full bg-slate-700 flex items-center justify-center">
              <User className="h-3 w-3 text-slate-400" />
            </div>
          )}
          <span className="text-sm font-medium text-white">{message.senderName}</span>
        </div>
        <p className="text-sm text-slate-300 line-clamp-3">{message.messageText}</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <Users2 className="h-3 w-3" />
          <span>{message.squadName}</span>
        </div>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatTimeAgo(message.createdAt)}
        </span>
      </div>
    </div>
  );
}
